import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import {
  getSentryReportSchema,
  SENTRY_REPORT_MESSAGE_MAX_LENGTH,
  SENTRY_REPORT_NAME_MAX_LENGTH,
  type SentryReportFormData,
} from "Components/Support/sentryReportSchema";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import { SENTRY_REPORT_FORM_ID } from "Components/Support/supportFormIds";
import type { SupportFormStatus } from "Components/Support/SupportFormStatus";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { getReportableUrl } from "Infrastructure/Utils/SupportUtils";
import { Resources, useResource } from "Translations/Resources";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const RESOURCES = Resources.Sentry;

const DEFAULT_VALUES: SentryReportFormData = {
  message: "",
  name: "",
  email: "",
};

/**
 * Unlike the support-request step this one takes no retry values: the report is
 * handed to the Sentry SDK synchronously, so the only way it can fail is the SDK not
 * being initialised at all — which pressing send again would not change.
 */
type Props = {
  onStatusChange: (status: SupportFormStatus) => void;
  onSubmitted: (result: SupportFlowResult) => void;
};

export const SentryReportFormStep = ({
  onStatusChange,
  onSubmitted,
}: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const isAnonymous = !user;

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<SentryReportFormData>({
    resolver: zodResolver(getSentryReportSchema(t)),
    defaultValues: DEFAULT_VALUES,
  });

  // React Hook Form exposes these only as derived render state, so lifting them to the
  // dialog — which owns the footer buttons and the discard-changes guard — takes an
  // effect. Validation is async, and the footer's submit button locks on `isSubmitting`:
  // without it a fast double-click sends two reports (and two replays) for the same thing.
  useEffect(() => {
    onStatusChange({ isDirty, isBusy: isSubmitting });
  }, [isDirty, isSubmitting, onStatusChange]);

  const onSubmit = ({ message, email, name }: SentryReportFormData) => {
    // With Sentry uninitialized (local dev, where VITE_SENTRY_IS_ENABLED is false)
    // captureFeedback drops the event on the floor — don't claim it was sent.
    if (!Sentry.isInitialized()) {
      onSubmitted({
        variant: "error",
        message: t(RESOURCES.ErrorMessageText),
        canRetry: false,
      });

      return;
    }

    Sentry.captureFeedback(
      {
        message,
        name: isAnonymous ? name : `${user.firstName} ${user.lastName}`.trim(),
        email: isAnonymous ? email : user.login,
        url: getReportableUrl(),
        // `source` MUST stay "api": the replay integration only flushes the
        // buffered recording for that exact value (see its beforeSendFeedback
        // hook). Replay runs in buffer mode here (replaysSessionSampleRate: 0),
        // so without the flush the report arrives with no session replay at all.
        source: "api",
      },
      { includeReplay: true },
    );

    onSubmitted({
      variant: "success",
      message: t(RESOURCES.SuccessMessageText),
      canRetry: false,
    });
  };

  const messageLength = watch("message")?.length ?? 0;

  return (
    <form id={SENTRY_REPORT_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={2}>
        {isAnonymous && (
          <>
            <FormInput
              control={control}
              name="name"
              label={t(RESOURCES.NameLabel)}
              placeholder={t(RESOURCES.NamePlaceholder)}
              maxLength={SENTRY_REPORT_NAME_MAX_LENGTH}
              formControlClass="fullWidth"
              fullWidth
            />
            <FormInput
              control={control}
              name="email"
              type="email"
              label={t(RESOURCES.EmailLabel)}
              placeholder={t(RESOURCES.EmailPlaceholder)}
              formControlClass="fullWidth"
              fullWidth
            />
          </>
        )}
        <FormInput
          control={control}
          name="message"
          label={t(RESOURCES.MessageLabel)}
          placeholder={t(RESOURCES.MessagePlaceholder)}
          multiline
          rows={6}
          maxLength={SENTRY_REPORT_MESSAGE_MAX_LENGTH}
          formControlClass="fullWidth"
          fullWidth
          helperText={
            <Box display="flex" justifyContent="flex-end" component="span">
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {t(RESOURCES.MessageCharCount, {
                  count: messageLength,
                  maxCount: SENTRY_REPORT_MESSAGE_MAX_LENGTH,
                })}
              </Typography>
            </Box>
          }
        />
      </Stack>
    </form>
  );
};
